import React, { useState, useMemo, useCallback, useRef } from 'react';


const ALPHA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

function nextCohortName(existingNames) {
  for (let i = 0; i < ALPHA.length; i++) {
    const name = 'Cohort ' + ALPHA[i];
    if (!existingNames.includes(name)) return name;
  }
  return 'Cohort ' + Date.now();
}

export default function CohortBuilder({
  school,
  allStudents,
  initialCohorts = [],
  onSave,
  onCancel,
}) {
  const schoolStudents = useMemo(
    () => allStudents.filter(s => String(s.schoolId) === String(school.id)),
    [allStudents, school.id]
  );

  const grades = useMemo(() => {
    const gs = [...new Set(schoolStudents.map(s => s.grade).filter(Boolean))];
    return gs.sort();
  }, [schoolStudents]);

  // cohorts: [{ id, name, studentIds[] }]
  const [cohorts, setCohorts] = useState(() => {
    if (initialCohorts.length > 0) {
      return initialCohorts.map(c => ({
        id: c.id,
        name: c.name,
        studentIds: [...(c.studentIds || [])].map(String),
        schoolId: c.schoolId,
        schoolName: c.schoolName,
        status: c.status,
        spocId: c.spocId,
        spocName: c.spocName,
        counselorIds: c.counselorIds,
        auditorAssignments: c.auditorAssignments,
        levelYear: c.levelYear,
        branchId: c.branchId,
      }));
    }
    return [];
  });

  const [rosterSearch, setRosterSearch] = useState('');
  const [gradeFilter, setGradeFilter] = useState('All');
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [autoSaveFlash, setAutoSaveFlash] = useState(false);
  const autoSaveTimer = useRef(null);

  // which student is being dragged
  const dragStudentId = useRef(null);
  const dragFromCohortId = useRef(null); // null = from roster/unassigned

  // which cohort is being dragged over
  const [dragOverCohortId, setDragOverCohortId] = useState(null);

  const assignedIds = useMemo(
    () => new Set(cohorts.flatMap(c => c.studentIds).map(String)),
    [cohorts]
  );

  const unassignedStudents = useMemo(() => {
    return schoolStudents.filter(s => !assignedIds.has(String(s.id)));
  }, [schoolStudents, assignedIds]);

  const filteredRoster = useMemo(() => {
    let list = unassignedStudents;
    if (gradeFilter !== 'All') list = list.filter(s => s.grade === gradeFilter);
    const q = rosterSearch.trim().toLowerCase();
    if (q) list = list.filter(s => s.name.toLowerCase().includes(q) || s.grade.toLowerCase().includes(q));
    return list;
  }, [unassignedStudents, gradeFilter, rosterSearch]);

  function triggerAutoSave() {
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      setAutoSaveFlash(true);
      setTimeout(() => setAutoSaveFlash(false), 1500);
    }, 400);
  }

  function addCohort() {
    const name = nextCohortName(cohorts.map(c => c.name));
    setCohorts(prev => [...prev, { id: 'cb-' + Date.now(), name, studentIds: [], levelYear: 'Year 1' }]);
    triggerAutoSave();
  }

  function setCohortYear(cohortId, year) {
    setCohorts(prev => prev.map(c => c.id === cohortId ? { ...c, levelYear: year } : c));
    triggerAutoSave();
  }

  function deleteCohort(cohortId) {
    setCohorts(prev => prev.filter(c => c.id !== cohortId));
    triggerAutoSave();
  }

  function commitNameEdit(cohortId) {
    const val = editingNameValue.trim();
    if (val) {
      setCohorts(prev => prev.map(c => c.id === cohortId ? { ...c, name: val } : c));
      triggerAutoSave();
    }
    setEditingNameId(null);
  }

  function removeStudentFromCohort(studentId, cohortId) {
    const sid = String(studentId);
    setCohorts(prev => prev.map(c =>
      c.id === cohortId
        ? { ...c, studentIds: c.studentIds.filter(id => String(id) !== sid) }
        : c
    ));
    triggerAutoSave();
  }

  function assignStudentToCohort(studentId, toCohortId) {
    const sid = String(studentId);
    // hard block: if already in a cohort, do nothing (one-cohort-per-student)
    if ([...assignedIds].some(id => String(id) === sid)) return;
    setCohorts(prev => prev.map(c =>
      c.id === toCohortId ? { ...c, studentIds: [...c.studentIds, sid] } : c
    ));
    triggerAutoSave();
  }

  function moveStudentBetweenCohorts(studentId, fromCohortId, toCohortId) {
    if (fromCohortId === toCohortId) return;
    const sid = String(studentId);
    setCohorts(prev => prev.map(c => {
      if (c.id === fromCohortId) return { ...c, studentIds: c.studentIds.filter(id => String(id) !== sid) };
      if (c.id === toCohortId) return { ...c, studentIds: [...c.studentIds, sid] };
      return c;
    }));
    triggerAutoSave();
  }

  // --- drag handlers ---
  const onDragStart = useCallback((studentId, fromCohortId) => {
    dragStudentId.current = studentId;
    dragFromCohortId.current = fromCohortId;
  }, []);

  const onDropOnCohort = useCallback((toCohortId) => {
    const sid = dragStudentId.current;
    const fromId = dragFromCohortId.current;
    if (!sid) return;
    if (fromId === null) {
      assignStudentToCohort(sid, toCohortId);
    } else {
      moveStudentBetweenCohorts(sid, fromId, toCohortId);
    }
    dragStudentId.current = null;
    dragFromCohortId.current = null;
    setDragOverCohortId(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohorts]);

  function handleSave() {
    let resolvedBranchId = school?.branch_id || null;
    const result = cohorts.map(c => ({
      id: c.id,
      name: c.name,
      schoolId: c.schoolId || school.id,
      schoolName: c.schoolName || school.school_name || school.name || '',
      branchId: c.branchId || resolvedBranchId,
      status: c.status || 'Active',
      studentIds: c.studentIds,
      spocId: c.spocId || null,
      spocName: c.spocName || null,
      counselorIds: c.counselorIds || [],
      auditorAssignments: c.auditorAssignments || [],
      levelYear: c.levelYear ?? null,
    }));
    onSave(result);
  }

  const totalAssigned = assignedIds.size;
  const totalUnassigned = unassignedStudents.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#f8fafc', fontFamily: "'Outfit', sans-serif" }}>

      {/* ---- Header ---- */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 28px', height: '60px', minHeight: '60px',
        background: '#0147a2', color: '#fff', boxShadow: '0 2px 8px rgba(1,71,162,0.3)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 0' }}>
          <span style={{ fontSize: '13px', opacity: 0.75 }}>{school.school_name || school.name}</span>
          {(school.branch_name || school.branchName) && (
            <>
              <span style={{ margin: '0 8px', opacity: 0.4 }}>·</span>
              <span style={{
                fontSize: '12px',
                fontWeight: 600,
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.35)',
                borderRadius: '999px',
                padding: '2px 10px',
              }}>
                {school.branch_name || school.branchName}
              </span>
            </>
          )}
          <span style={{ margin: '0 8px', opacity: 0.4 }}>›</span>
          <span style={{ fontSize: '14px', fontWeight: '600' }}>Cohort Builder</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {autoSaveFlash && (
            <span style={{ fontSize: '12px', opacity: 0.8, display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
              Auto-saved
            </span>
          )}
          <button
            onClick={onCancel}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', padding: '6px 14px', borderRadius: '7px', cursor: 'pointer', fontSize: '13px' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{ background: '#f89f09', border: 'none', color: '#fff', padding: '7px 18px', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
          >
            Save & Close
          </button>
        </div>
      </div>

      {/* ---- Summary bar ---- */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '24px',
        padding: '10px 28px', background: '#fff', borderBottom: '1px solid #e2e8f0',
        fontSize: '13px', color: '#475569', flexShrink: 0,
      }}>
        {(school.branch_name || school.branchName || school.branch_id) && (
          <>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: '#e8f0fe',
              border: '1px solid #c5d7f5',
              borderRadius: '999px',
              padding: '3px 10px',
              color: '#0147a2',
              fontWeight: 600,
              fontSize: '12px',
            }}>
              <span style={{ fontWeight: 500, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '11px' }}>
                Branch
              </span>
              {school.branch_name || school.branchName || `Branch #${school.branch_id}`}
            </span>
            <span>·</span>
          </>
        )}
        <span><strong style={{ color: '#0147a2' }}>{cohorts.length}</strong> cohort{cohorts.length !== 1 ? 's' : ''}</span>
        <span>·</span>
        <span><strong style={{ color: '#059669' }}>{totalAssigned}</strong> assigned</span>
        <span>·</span>
        <span><strong style={{ color: '#b45309' }}>{totalUnassigned}</strong> unassigned</span>
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#94a3b8' }}>
          Drag students from the left panel into a cohort column. One cohort per student.
        </span>
      </div>

      {/* ---- Split layout ---- */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ---- Left roster panel ---- */}
        <div style={{
          width: '290px', minWidth: '290px', background: '#fff',
          borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column',
          flexShrink: 0,
        }}>
          <div style={{ padding: '16px 16px 10px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>Unassigned Students</span>
              <span style={{
                background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a',
                borderRadius: '20px', padding: '2px 9px', fontSize: '12px', fontWeight: '600'
              }}>
                {totalUnassigned}
              </span>
            </div>

            <input
              type="text"
              placeholder="Search students…"
              value={rosterSearch}
              onChange={e => setRosterSearch(e.target.value)}
              style={{
                width: '100%', padding: '7px 10px', fontSize: '13px',
                border: '1px solid #e2e8f0', borderRadius: '7px', outline: 'none',
                boxSizing: 'border-box', color: '#0f172a', background: '#f8fafc',
              }}
            />
          </div>

          {/* Grade filter — sticky */}
          <div style={{
            padding: '8px 16px', borderBottom: '1px solid #f1f5f9',
            display: 'flex', flexWrap: 'wrap', gap: '6px', flexShrink: 0,
            position: 'sticky', top: 0, background: '#fff', zIndex: 1,
          }}>
            {['All', ...grades].map(g => (
              <button
                key={g}
                onClick={() => setGradeFilter(g)}
                style={{
                  padding: '3px 10px', fontSize: '12px', borderRadius: '20px', cursor: 'pointer',
                  border: '1px solid',
                  borderColor: gradeFilter === g ? '#0147a2' : '#e2e8f0',
                  background: gradeFilter === g ? '#0147a2' : '#f8fafc',
                  color: gradeFilter === g ? '#fff' : '#475569',
                  fontWeight: gradeFilter === g ? '600' : '400',
                  transition: 'all 0.15s',
                }}
              >
                {g}
              </button>
            ))}
          </div>

          {/* Roster list — scrollable */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
            {filteredRoster.length === 0 && (
              <div style={{ padding: '24px 12px', textAlign: 'center', color: '#94a3b8', fontSize: '13px', fontStyle: 'italic' }}>
                {totalUnassigned === 0 ? 'All students assigned!' : 'No students match filter.'}
              </div>
            )}
            {filteredRoster.map(s => (
              <StudentChip
                key={s.id}
                student={s}
                fromCohortId={null}
                onDragStart={onDragStart}
              />
            ))}
          </div>
        </div>

        {/* ---- Right cohort grid ---- */}
        <div style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', padding: '20px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(cohorts.length + 1, 4)}, minmax(240px, 1fr))`,
            gap: '16px',
            alignItems: 'start',
            minWidth: cohorts.length >= 4 ? `${(cohorts.length + 1) * 256}px` : 'auto',
          }}>
            {cohorts.map(cohort => (
              <CohortColumn
                key={cohort.id}
                cohort={cohort}
                schoolStudents={schoolStudents}
                isDragOver={dragOverCohortId === cohort.id}
                editingNameId={editingNameId}
                editingNameValue={editingNameValue}
                onDragOverEnter={() => setDragOverCohortId(cohort.id)}
                onDragOverLeave={() => setDragOverCohortId(null)}
                onDrop={() => onDropOnCohort(cohort.id)}
                onDragStart={onDragStart}
                onRemoveStudent={removeStudentFromCohort}
                onStartNameEdit={(id, name) => { setEditingNameId(id); setEditingNameValue(name); }}
                onNameChange={setEditingNameValue}
                onNameCommit={commitNameEdit}
                onDelete={deleteCohort}
                onYearChange={setCohortYear}
              />
            ))}

            {/* Add cohort placeholder */}
            <button
              onClick={addCohort}
              style={{
                border: '2px dashed #cbd5e1', borderRadius: '12px', background: 'transparent',
                cursor: 'pointer', padding: '32px 16px', color: '#64748b', fontSize: '14px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
                transition: 'all 0.15s', minHeight: '120px',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#0147a2'; e.currentTarget.style.color = '#0147a2'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#64748b'; }}
            >
              <span style={{ fontSize: '24px', lineHeight: 1 }}>+</span>
              <span style={{ fontWeight: '500' }}>Add Cohort</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StudentChip({ student, fromCohortId, onDragStart, onRemove }) {
  return (
    <div
      draggable
      onDragStart={() => onDragStart(student.id, fromCohortId)}
      style={{
        display: 'flex', alignItems: 'center', gap: '7px',
        padding: '6px 10px', marginBottom: '4px',
        background: fromCohortId ? '#f8fafc' : '#fffbeb',
        border: fromCohortId ? '1px solid #e2e8f0' : '1px solid #fde68a',
        borderRadius: '8px', cursor: 'grab', userSelect: 'none',
        fontSize: '13px', color: '#1e293b',
      }}
    >
      <span style={{ color: '#94a3b8', fontSize: '11px', lineHeight: 1 }}>⠿</span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{student.name}</span>
      <span style={{
        background: fromCohortId ? '#f1f5f9' : '#fef3c7',
        color: fromCohortId ? '#475569' : '#b45309',
        border: fromCohortId ? '1px solid #e2e8f0' : '1px solid #fde68a',
        borderRadius: '4px', padding: '1px 5px', fontSize: '11px', whiteSpace: 'nowrap', flexShrink: 0,
      }}>
        {student.grade}
      </span>
      {onRemove && (
        <button
          onClick={e => { e.stopPropagation(); onRemove(student.id); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: '#94a3b8', fontSize: '13px', lineHeight: 1 }}
          title="Return to unassigned"
        >
          ✕
        </button>
      )}
    </div>
  );
}

function CohortColumn({
  cohort, schoolStudents,
  isDragOver, editingNameId, editingNameValue,
  onDragOverEnter, onDragOverLeave, onDrop, onDragStart,
  onRemoveStudent, onStartNameEdit, onNameChange, onNameCommit, onDelete, onYearChange,
}) {
  const [colSearch, setColSearch] = React.useState('');
  const rosterById = React.useMemo(
    () => new Map(schoolStudents.map(s => [String(s.id), s])),
    [schoolStudents]
  );
  const allStudents = (cohort.studentIds || []).map(id => {
    const key = String(id);
    return rosterById.get(key) || { id: key, name: `Student #${key}`, grade: '' };
  });
  const students = colSearch.trim()
    ? allStudents.filter(s => s.name.toLowerCase().includes(colSearch.trim().toLowerCase()) || s.grade.toLowerCase().includes(colSearch.trim().toLowerCase()))
    : allStudents;

  return (
    <div
      onDragOver={e => { e.preventDefault(); onDragOverEnter(); }}
      onDragLeave={onDragOverLeave}
      onDrop={e => { e.preventDefault(); onDrop(); }}
      style={{
        background: '#fff', borderRadius: '12px', border: isDragOver ? '2px solid #0147a2' : '1px solid #e2e8f0',
        boxShadow: isDragOver ? '0 0 0 3px rgba(1,71,162,0.12)' : '0 1px 4px rgba(0,0,0,0.06)',
        display: 'flex', flexDirection: 'column', position: 'relative',
        transition: 'border 0.15s, box-shadow 0.15s',
      }}
    >
      {/* Drop overlay — covers full card body when drag-over */}
      {isDragOver && (
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '12px',
          background: 'rgba(1,71,162,0.04)', zIndex: 1, pointerEvents: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#0147a2', background: '#fff', padding: '6px 14px', borderRadius: '20px', border: '1px solid #bfdbfe' }}>
            Drop here
          </span>
        </div>
      )}

      {/* Card header */}
      <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: allStudents.length > 0 ? '8px' : '0' }}>
          {editingNameId === cohort.id ? (
            <input
              autoFocus
              value={editingNameValue}
              onChange={e => onNameChange(e.target.value)}
              onBlur={() => onNameCommit(cohort.id)}
              onKeyDown={e => { if (e.key === 'Enter') onNameCommit(cohort.id); if (e.key === 'Escape') onNameCommit(cohort.id); }}
              style={{ flex: 1, fontSize: '14px', fontWeight: '600', border: '1px solid #0147a2', borderRadius: '5px', padding: '2px 6px', outline: 'none', color: '#0f172a' }}
            />
          ) : (
            <span style={{ flex: 1, fontSize: '14px', fontWeight: '600', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {cohort.name}
            </span>
          )}
          <button
            onClick={() => onStartNameEdit(cohort.id, cohort.name)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#94a3b8', fontSize: '13px' }}
            title="Rename cohort"
          >
            ✎
          </button>
          <span style={{
            background: '#f0f9ff', color: '#0147a2', border: '1px solid #bfdbfe',
            borderRadius: '20px', padding: '1px 8px', fontSize: '11px', fontWeight: '600', flexShrink: 0,
          }}>
            {students.length}
          </span>
          <button
            onClick={() => onDelete(cohort.id)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#cbd5e1', fontSize: '13px' }}
            title="Delete cohort"
          >
            🗑
          </button>
        </div>
        <div style={{ marginTop: '6px' }}>
          <select
            value={cohort.levelYear ?? ''}
            onChange={e => onYearChange(cohort.id, e.target.value)}
            style={{
              fontSize: '11px', padding: '2px 6px', borderRadius: '5px',
              border: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569',
              cursor: 'pointer', outline: 'none',
            }}
          >
            {!cohort.levelYear && <option value="" disabled>-- Select year --</option>}
            <option value="Year 1">Year 1 — Glosco Councils</option>
            <option value="Year 2">Year 2 — Glosco Policy Studio</option>
          </select>
        </div>
        {allStudents.length > 0 && (
          <input
            type="text"
            placeholder="Search in cohort…"
            value={colSearch}
            onChange={e => setColSearch(e.target.value)}
            style={{
              width: '100%', padding: '5px 9px', fontSize: '12px',
              border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none',
              boxSizing: 'border-box', color: '#0f172a', background: '#f8fafc',
            }}
          />
        )}
      </div>

      {/* Student list */}
      <div style={{ padding: '8px', maxHeight: '340px', overflowY: 'auto', flex: 1 }}>
        {students.length === 0 ? (
          <div style={{
            border: '2px dashed #e2e8f0', borderRadius: '8px', padding: '20px 12px',
            textAlign: 'center', color: '#94a3b8', fontSize: '12px',
          }}>
            {allStudents.length > 0 ? 'No match' : 'Drop students here'}
          </div>
        ) : (
          students.map(s => (
            <StudentChip
              key={s.id}
              student={s}
              fromCohortId={cohort.id}
              onDragStart={onDragStart}
              onRemove={(sid) => onRemoveStudent(sid, cohort.id)}
            />
          ))
        )}
      </div>

      {/* Card footer */}
      <div style={{ padding: '8px 14px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ fontSize: '11px', color: '#94a3b8' }}>{students.length} student{students.length !== 1 ? 's' : ''}</span>
        <span style={{ fontSize: '11px', color: '#cbd5e1' }}>No max limit</span>
      </div>
    </div>
  );
}
